import { useLazyQuery } from "@apollo/client";
import { Checkbox, createStyles, IconButton, makeStyles, TextField } from "@material-ui/core";
import { FormattedTableCell } from "components/Table/Row";
import { Column } from "components/Table/Table";
import { table } from "console";
import useText from "constants/text";
import { GET_HUBS_BY_CITY } from "domains/Task/graphql/queries";
import HTTP_STATUS_CODES from "domains/Task/types/Column";
import { Hub } from "generated/graphql";
import { EditIcon, RemoveIcon } from "icons";
import React from "react";
import Autocomplete from "ui/Autocomplete";

export interface SimpleRowProps<RowData, RowDataKey extends keyof RowData> {
  columns?: Array<Column<RowData>>;
  rowContent?: RowData;
  rowsIdentifier?: RowDataKey;
  onRowChecked?(data: RowData): void;
  handleGetReceiverHub?(data: Hub, rowId: string): void;
  handleHubSelect?(data: Hub): void;
  onRowClick?(data: RowData): void;
  onRowDelete?(data: RowData): void;
  onRowEdit?(data: RowData): void;
  handleRowSelectClick?(event: React.MouseEvent<unknown, MouseEvent>, id: string): void;
  rowsCount?: number;
  selectRowsCount?: number;
  hasSelection?: boolean;
  isShowRelay?: boolean;
  hasChecked?: boolean;
  isRelayPoint?: boolean;
  isRowSelected?(rowIdentifier: any): boolean;
  relayPointCity?: any[];
}

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      width: "100%",
    },
    th: {
      textAlign: "left",
      padding: 8,
    },
    tdCity: {
      textAlign: "left",
      padding: 8,
      fontSize: 14,
      fontStretch: "normal",
      fontStyle: "normal",
      lineHeight: "normal",
      letterSpacing: "normal",
      color: "#757a93",
      width: "18%",
      position: "absolute",
      top: 6,
      right: "232px",
      '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
        padding: '0px !important',
      }
    },
    tdRP: {
      textAlign: "left",
      padding: 8,
      fontFamily: "Montserrat",
      fontSize: 14,
      fontStretch: "normal",
      fontStyle: "normal",
      lineHeight: "normal",
      letterSpacing: "normal",
      color: "#757a93",
      width: "18%",
      position: "absolute",
      top: 6,
      right: "11px",
      '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
        padding: '0px !important',
      }
    },
    td: {
      textAlign: "left",
      padding: '22px  22px 0',
      fontFamily: "Montserrat",
      fontSize: 14,
      fontStretch: "normal",
      fontStyle: "normal",
      lineHeight: "normal",
      letterSpacing: "normal",
      color: "#757a93",
      margin: "0 10px"
    },
    visuallyHidden: {
      border: 0,
      clip: "rect(0 0 0 0)",
      height: 1,
      margin: -1,
      overflow: "hidden",
      padding: 0,
      position: "absolute",
      top: 20,
      width: 1,
    },
    trStyle: {
      width: '924px',
      height: '60px',
      flexGrow: 0,
      margin: '19px 36px 14px 25px',
      padding: '9px 13px 9.6px',
      borderRadius: '5px',
      border: 'solid 1px rgba(57, 158, 191, 0.23)'
    }

  })
);

export function SimpleRow<RowData, RowDataKey extends keyof RowData>({
  rowContent,
  columns,
  rowsIdentifier,
  onRowChecked,
  hasSelection,
  hasChecked,
  isShowRelay,
  onRowClick,
  onRowDelete,
  onRowEdit,
  handleRowSelectClick,
  isRowSelected,
  rowsCount,
  selectRowsCount,
  isRelayPoint,
  relayPointCity,
  handleHubSelect,
  handleGetReceiverHub
}: SimpleRowProps<RowData, RowDataKey>) {
  const styles = useStyles();
  const rowId = rowContent[rowsIdentifier] as unknown as string;
  const isSelected = isRowSelected(rowId);
  const { text } = useText();
  const [relays, setRelays] = React.useState([]);
  const [cityValue, setCityValue] = React.useState<string>(null);
  const [getAllHubsByCity, { loading, data: hubsByCity }] = useLazyQuery(GET_HUBS_BY_CITY);
  const [inputValueRelayPoint, setInputValueRelayPoint] = React.useState<Hub>(null);
  const [rowRelayPoint, setRowRelayPoint] = React.useState<any[]>([]);

  const handleRowClick = (event: any) => {
    event.stopPropagation();
    onRowClick && onRowClick(rowContent);
  };
  React.useEffect(() => {
    if (cityValue) {
      getAllHubsByCity({
        variables: {
          queries: {
            city: cityValue,
          },
        },
      });
    }
  }, [cityValue]);

  React.useEffect(() => {
    if (inputValueRelayPoint) {
      handleHubSelect(inputValueRelayPoint);
    }
  }, [inputValueRelayPoint]);

  React.useEffect(() => {
    if (hubsByCity) {
      const getHubs = hubsByCity?.getAllHubsByCity;
      if (getHubs?.code === HTTP_STATUS_CODES.OK) {
        setRelays(getHubs?.data);
      }
    }
  }, [hubsByCity]);
  const handleSelect = (event: any, name: string) => {
    setCityValue(name);
  };
  const handleOnInputRelaypointChange = (event: any, newInputValue: Hub) => {
    handleGetReceiverHub(newInputValue, rowId);
  };

  const handleCheckboxClick = (event: any) => {
    event.stopPropagation();
    handleRowSelectClick(event, rowId);
    onRowChecked && onRowChecked(rowContent);
  };

  const handleEditIconClick = (event: any) => {
    event.stopPropagation();
    onRowEdit(rowContent);
  };
  const handleDeleteIconClick = (event: any) => {
    event.stopPropagation();
    onRowDelete(rowContent);
  };

  return (
    <>
      <tr role="row" onClick={handleRowClick} className={styles.trStyle}>
        {hasSelection && (
          <td align="left">
            <Checkbox
              color="primary"
              checked={isSelected || rowsCount === selectRowsCount}
              onClick={handleCheckboxClick}
            />
          </td>
        )}
        {columns?.map((column) => (
          <td key={column.field} align="left" className={styles.td}>
            <FormattedTableCell column={column} row={rowContent} />
          </td>
        ))}
        {(onRowDelete || onRowEdit) && (
          <td align="right">
            {onRowEdit && (
              <IconButton onClick={handleEditIconClick}>
                <EditIcon width={20} />
              </IconButton>
            )}

            {onRowDelete && (
              <IconButton onClick={handleDeleteIconClick}>
                <RemoveIcon width={20} height={20} />
              </IconButton>
            )}
          </td>
        )}
        {isRelayPoint && isShowRelay && (
          <td className={styles.tdCity}>
            <Autocomplete
              disabled={!isSelected}
              options={relayPointCity}
              value={cityValue}
              onChange={handleSelect}
              label={text.all.city}
              name="city"
              getOptionLabel={(option: any) => option}
              renderInput={(params: any) => <TextField {...params} variant="outlined" />}
            />
          </td>
        )}
        {isRelayPoint && isShowRelay && (
          <td className={styles.tdRP}>
            <Autocomplete
              disabled={!isSelected || loading || cityValue === null}
              options={relays}
              value={inputValueRelayPoint?.location?.address}
              onChange={handleOnInputRelaypointChange}
              label={text.all.relay_point}
              name="relay"
              getOptionLabel={(option: any) => option.name}
              renderInput={(params: any) => <TextField {...params} variant="outlined" />}
            />
          </td>
        )}
      </tr>
    </>
  );
}
